// DEPLOY: /var/www/woody.wtf/frontend/src/pages/TradingFx.js
// Trading > Currency (/trading/fx). Cash held in each currency (AUD, USD) with its value in
// the base currency, the live AUD.USD rate from IBKR's FX market (IDEALPRO), and a converter:
// pick From / To and an amount, see roughly what you'll receive, Preview (IBKR what-if: the
// commission) and Convert, at market or a limit rate. Converting here uses IBKR's FX rates
// instead of a bank's or a broker's conversion margin. The order quantity is always in AUD
// (the pair's base currency); USD -> AUD buys AUD. Conversions appear in the site order log.
// Data: /api/trading/<broker>/fx and /fx/convert (trading.py -> ibkr_bridge.py). Refreshes every 10s.
// Admin-only for now (useTradingAccount guard; API is @admin_required in trading.py).
import React, { useState, useEffect, useCallback } from 'react';
import Navbar from '../components/Navbar';
import TradingTabs from '../components/TradingTabs';
import api from '../api';
import { s, isNum, num, errText, Tile, useTradingAccount, StatusBar } from '../components/tradingShared';

const FX_REFRESH_MS = 10000;
const rate = v => isNum(v) ? Number(v).toFixed(5) : '—';

export default function TradingFx() {
  const t = useTradingAccount();
  const [fx, setFx]           = useState(null);
  const [err, setErr]         = useState('');
  const [updated, setUpdated] = useState(null);
  const [from, setFrom]       = useState('AUD');
  const [amount, setAmount]   = useState('');
  const [type, setType]       = useState('MARKET');
  const [limit, setLimit]     = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy]       = useState('');
  const [msg, setMsg]         = useState('');
  const [actErr, setActErr]   = useState('');

  useEffect(() => { document.title = 'Currency | woody.wtf'; }, []);

  const load = useCallback(async () => {
    if (!t.broker || !t.acc) return;
    try {
      setFx((await api.get(`/trading/${t.broker}/fx`, { params: { acc_id: t.acc.acc_id } })).data);
      setUpdated(new Date()); setErr('');
    } catch (e) { setErr(errText(e)); }
  }, [t.broker, t.acc]);
  useEffect(() => {
    load();
    const i = setInterval(load, FX_REFRESH_MS);
    return () => clearInterval(i);
  }, [load]);

  const to = from === 'AUD' ? 'USD' : 'AUD';
  const qt = fx?.quote || {};
  const live = qt.market_data_type === 'live';
  const amt = Number(amount) || 0;
  // AUD -> USD sells AUD at the bid; USD -> AUD buys AUD at the ask (or the limit rate).
  const useRate = type === 'LIMIT' ? Number(limit) || null : from === 'AUD' ? (qt.bid || qt.mid) : (qt.ask || qt.mid);
  const receive = useRate ? (from === 'AUD' ? amt * useRate : amt / useRate) : null;
  const ready = amt > 0 && (type === 'MARKET' || Number(limit) > 0) && !busy;
  const cash = fx?.cash || {}, rates = fx?.rates || {};

  const body = extra => ({ acc_id: t.acc.acc_id, from, to, amount: amt, order_type: type, limit_rate: limit, ...extra });
  const doPreview = async () => {
    setBusy('preview'); setActErr(''); setMsg('');
    try { setPreview((await api.post(`/trading/${t.broker}/fx/convert`, body({ preview: true }))).data); }
    catch (e) { setActErr(errText(e)); }
    finally { setBusy(''); }
  };
  const convert = async () => {
    const what = `Convert ${num(amt)} ${from} to ${to}${type === 'LIMIT' ? ` at a limit of ${limit} USD per AUD` : ' at market'}\n≈ ${num(receive)} ${to}`;
    if (!window.confirm(`LIVE currency conversion on account ${t.acc.acc_id}:\n\n${what}\n\nGo ahead?`)) return;
    setBusy('convert'); setActErr(''); setMsg('');
    try {
      setMsg((await api.post(`/trading/${t.broker}/fx/convert`, body())).data.message);
      setPreview(null); setAmount(''); load();
    } catch (e) { setActErr(errText(e)); }
    finally { setBusy(''); }
  };
  const flip = () => { setFrom(to); setPreview(null); };

  const kv = (k, v) => (
    <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '13px' }}>
      <span style={{ color: 'var(--text-muted)' }}>{k}</span><span style={s.mono}>{v}</span>
    </div>
  );

  return (
    <div style={s.page}>
      <Navbar />
      <div style={s.content}>
        <h1 style={s.heading}>Trading</h1>
        <TradingTabs />
        <StatusBar t={t} />
        {t.acc && (
          <>
            {err && <div style={{ ...s.err, marginBottom: '12px' }}>✕ {err}</div>}
            <div style={s.tiles}>
              {['AUD', 'USD'].map(c => (
                <Tile key={c} label={`${c} cash`} value={num(cash[c])}
                      sub={c !== 'AUD' && isNum(cash[c]) && rates[c] ? `≈ ${num(cash[c] * rates[c])} AUD` : c} />
              ))}
              <Tile label="1 AUD =" value={rate(qt.mid ?? qt.last ?? qt.close)} sub="USD (mid)" />
              <Tile label="1 USD =" value={isNum(qt.mid) ? rate(1 / qt.mid) : '—'} sub="AUD (mid)" />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: '20px', alignItems: 'start' }}>
              <div style={s.card}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                  <span style={s.title}>AUD.USD</span>
                  {fx && <span style={live ? s.pill('var(--success-dim)', 'var(--success)') : s.pill('var(--bg)', 'var(--text-muted)')}>
                    {(qt.market_data_type || 'no data').toUpperCase()}</span>}
                  <span style={{ ...s.meta, marginLeft: 'auto' }}>{updated ? `Updated ${updated.toLocaleTimeString('en-GB')}` : 'Loading…'}</span>
                </div>
                {kv('Bid (you sell AUD at)', rate(qt.bid))}
                {kv('Ask (you buy AUD at)', rate(qt.ask))}
                {kv('Mid', rate(qt.mid))}
                {kv('Spread', isNum(qt.bid) && isNum(qt.ask) ? `${((qt.ask - qt.bid) * 10000).toFixed(1)} pips` : '—')}
                {kv('Previous close', rate(qt.close))}
                <div style={{ ...s.meta, marginTop: '10px' }}>
                  Rates are USD per 1 AUD from IBKR's FX market. Small conversions go through as odd lots, which can price a little wider than the quote above.
                </div>
              </div>

              <div style={s.card}>
                <div style={{ ...s.title, marginBottom: '12px' }}>Convert</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '10px', alignItems: 'end', marginBottom: '12px' }}>
                  <div>
                    <div style={s.label}>From</div>
                    <div style={{ ...s.input, ...s.mono, fontWeight: 600 }}>{from}</div>
                  </div>
                  <button style={{ ...s.btn, padding: '6px 10px' }} onClick={flip} title="Swap">⇄</button>
                  <div>
                    <div style={s.label}>To</div>
                    <div style={{ ...s.input, ...s.mono, fontWeight: 600 }}>{to}</div>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '8px' }}>
                  <div>
                    <div style={s.label}>Amount ({from})</div>
                    <input style={s.input} type="number" min="0" step="any" value={amount} onChange={e => { setAmount(e.target.value); setPreview(null); }} />
                    {isNum(cash[from]) && <div style={{ ...s.meta, marginTop: '4px' }}>
                      <span style={s.link} onClick={() => { setAmount(String(Math.max(0, Math.floor(cash[from])))); setPreview(null); }}>Max {num(cash[from])}</span></div>}
                  </div>
                  <div>
                    <div style={s.label}>Order type</div>
                    <select style={{ ...s.select, width: '100%' }} value={type} onChange={e => { setType(e.target.value); setPreview(null); }}>
                      <option value="MARKET">Market</option><option value="LIMIT">Limit rate</option>
                    </select>
                  </div>
                </div>
                {type === 'LIMIT' && (
                  <div style={{ marginBottom: '8px' }}>
                    <div style={s.label}>Limit rate (USD per AUD) - {from === 'AUD' ? 'the lowest you will sell AUD at' : 'the highest you will pay for AUD'}</div>
                    <input style={s.input} type="number" min="0" step="0.00001" value={limit} onChange={e => { setLimit(e.target.value); setPreview(null); }}
                           placeholder={rate(from === 'AUD' ? qt.bid : qt.ask)} />
                  </div>
                )}
                <div style={{ padding: '10px 12px', background: 'var(--bg)', borderRadius: 'var(--radius)', margin: '6px 0 12px' }}>
                  {kv('You receive about', isNum(receive) && amt ? `${num(receive)} ${to}` : '—')}
                  {kv('At rate', rate(useRate))}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button style={{ ...s.btn, flex: 1 }} onClick={doPreview} disabled={!ready}>{busy === 'preview' ? 'Checking…' : 'Preview'}</button>
                  <button onClick={convert} disabled={!ready}
                          style={{ ...s.btn, flex: 2, border: 'none', fontSize: '13px', fontWeight: 600,
                                   background: ready ? 'var(--accent)' : 'var(--border)', color: ready ? '#fff' : 'var(--text-muted)',
                                   cursor: ready ? 'pointer' : 'default' }}>
                    {busy === 'convert' ? 'Converting…' : `Convert ${from} → ${to}`}
                  </button>
                </div>
                {msg && <div style={s.ok}>{msg}</div>}
                {actErr && <div style={s.err}>✕ {actErr}</div>}
                {preview && (
                  <div style={{ marginTop: '12px', padding: '10px 12px', background: 'var(--bg)', borderRadius: 'var(--radius)' }}>
                    <div style={{ ...s.title, fontSize: '12px', marginBottom: '6px' }}>Preview - nothing converted</div>
                    {kv('IBKR order', `${preview.side} ${num(preview.qty, 0)} AUD.USD`)}
                    {kv('Commission', isNum(preview.preview?.commission) ? `${num(preview.preview.commission)} ${preview.preview.commissionCurrency || ''}`
                      : isNum(preview.preview?.minCommission) ? `${num(preview.preview.minCommission)} – ${num(preview.preview.maxCommission)} ${preview.preview.commissionCurrency || ''}` : '—')}
                    {preview.preview?.warningText && <div style={{ ...s.err, fontSize: '12px' }}>⚠ {preview.preview.warningText}</div>}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
